import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { USERS, USER_COLORS, BUD_CATS, CURRENCIES, PAY_METHODS } from "../shared/constants.js";
import { displayDate } from "../shared/utils";

export function BudgetForm({ date, user, onSave, onClose }) {
  const [form, setForm] = useState({ dir: "out", amount: "", currency: "EUR", category: "", method: "", notes: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.amount) return;
    onSave({ ...form, id: Date.now(), date, user, createdAt: new Date().toISOString() });
    onClose();
  };
  return (
    <>
      <Field label="Type">
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {["out", "in"].map(d => (
            <button key={d} onClick={() => set("dir", d)} style={{
              flex: 1, padding: "0.5rem",
              border: `2px solid ${form.dir === d ? (d === "out" ? "#b05050" : "#4a8a6a") : "var(--ink20)"}`,
              borderRadius: 3, cursor: "pointer", fontFamily: "var(--font-body)",
              background: form.dir === d ? (d === "out" ? "#b05050" : "#4a8a6a") : "transparent",
              color: form.dir === d ? "white" : "var(--ink)",
            }}>{d === "out" ? "Expense" : "Income"}</button>
          ))}
        </div>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.75rem" }}>
        <Field label="Amount"><Inp type="number" value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="0.00" /></Field>
        <Field label="Currency">
          <Sel value={form.currency} onChange={e => set("currency", e.target.value)}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </Sel>
        </Field>
      </div>
      <Field label="Category">
        <Sel value={form.category} onChange={e => set("category", e.target.value)}>
          <option value="">—</option>
          {BUD_CATS.map(c => <option key={c}>{c}</option>)}
        </Sel>
      </Field>
      <Field label="Payment method">
        <Sel value={form.method} onChange={e => set("method", e.target.value)}>
          <option value="">—</option>
          {PAY_METHODS.map(m => <option key={m}>{m}</option>)}
        </Sel>
      </Field>
      <Field label="Notes"><Inp value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Lidl groceries, drill bit set…" /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function BudgetCard({ entry, currentUser, onDelete }) {
  const isOut = entry.dir === "out";
  const isOwn = entry.user === currentUser;
  return (
    <div style={{ padding: "0.75rem 1rem", background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.5rem", border: "1px solid var(--ink10)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink50)" }}>
            {entry.category || "—"}{entry.method ? ` · ${entry.method}` : ""}
          </span>
          <Avatar user={entry.user} size={14} />
          <span style={{ fontSize: "0.65rem", color: USER_COLORS[entry.user] || "var(--ink50)" }}>{entry.user}</span>
        </div>
        {entry.notes && <div style={{ fontSize: "0.88rem", color: "var(--ink)", marginTop: 2 }}>{entry.notes}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <span style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", color: isOut ? "#b05050" : "#4a8a6a" }}>
          {isOut ? "−" : "+"}{Number(entry.amount).toFixed(2)} {entry.currency}
        </span>
        {isOwn && (
          <button onClick={() => onDelete(entry.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 4 }}>
            <Icon name="trash" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function BudgetTab({ date, user, budget, setBudget, searchQ }) {
  const [viewMonth, setViewMonth] = useState(false);
  const [filterUser, setFilterUser] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const month = date.slice(0, 7);

  let base = searchQ
    ? budget.filter(b => (b.notes || "").toLowerCase().includes(searchQ) || (b.category || "").toLowerCase().includes(searchQ))
    : viewMonth ? budget.filter(b => b.date.startsWith(month)) : budget.filter(b => b.date === date);
  if (filterUser !== "all") base = base.filter(b => b.user === filterUser);

  const totalIn = base.filter(b => b.dir === "in").reduce((s, b) => s + Number(b.amount), 0);
  const totalOut = base.filter(b => b.dir === "out").reduce((s, b) => s + Number(b.amount), 0);
  const del = (id) => setBudget(budget.filter(b => b.id !== id));

  return (
    <div>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => setViewMonth(false)} style={{ padding: "0.35rem 0.8rem", border: `1px solid ${!viewMonth ? "var(--accent)" : "var(--ink20)"}`, borderRadius: 3, cursor: "pointer", background: !viewMonth ? "var(--accent)" : "transparent", color: !viewMonth ? "var(--paper)" : "var(--ink)", fontSize: "0.8rem" }}>Day</button>
        <button onClick={() => setViewMonth(true)} style={{ padding: "0.35rem 0.8rem", border: `1px solid ${viewMonth ? "var(--accent)" : "var(--ink20)"}`, borderRadius: 3, cursor: "pointer", background: viewMonth ? "var(--accent)" : "transparent", color: viewMonth ? "var(--paper)" : "var(--ink)", fontSize: "0.8rem" }}>Month ({month})</button>
        <Sel value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All users</option>
          {USERS.map(u => <option key={u}>{u}</option>)}
        </Sel>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
        {[["In", totalIn, "#4a8a6a"], ["Out", totalOut, "#b05050"], ["Balance", totalIn - totalOut, totalIn - totalOut >= 0 ? "#4a8a6a" : "#b05050"]].map(([l, v, c]) => (
          <div key={l} style={{ flex: 1, background: "var(--paper-dark)", borderRadius: 4, padding: "0.6rem 0.8rem", border: "1px solid var(--ink10)", textAlign: "center" }}>
            <div style={{ fontSize: "0.65rem", color: "var(--ink50)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{l}</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", color: c, marginTop: 2 }}>{v.toFixed(0)}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>Shared · all users</div>
      {base.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No entries.</p>}
      {base.map(e => (
        <div key={e.id}>
          {(viewMonth || searchQ) && <div style={{ fontSize: "0.68rem", color: "var(--ink30)", marginBottom: 2 }}>{displayDate(e.date)}</div>}
          <BudgetCard entry={e} currentUser={user} onDelete={del} />
        </div>
      ))}

      {showForm && (
        <Modal title="New Budget Entry" onClose={() => setShowForm(false)}>
          <BudgetForm date={date} user={user} onSave={e => { setBudget([...budget, e]); setShowForm(false); }} onClose={() => setShowForm(false)} />
        </Modal>
      )}
    </div>
  );
}